import { DestroyRef, Directive, ElementRef, inject, input, OnInit, signal } from '@angular/core';
import { PAGE_SCROLLER_MIN_HEIGHT_RATIO, SCROLL_TO_TOP_CLASSES } from './scroll.constants';
import { hasScrollableOverflowY, ScrollService } from './scroll.service';

@Directive({
    selector: '[scrollToTopContainer]',
    host: {
        [`[class.${SCROLL_TO_TOP_CLASSES.CONTAINER}]`]: 'true',
        [`[class.${SCROLL_TO_TOP_CLASSES.SCROLLED}]`]: 'scrolled()',
    },
})
export class ScrollToTopContainerDirective implements OnInit {
    private readonly scrollService = inject(ScrollService);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    /** Scroll distance that reveals the scroll-to-top button. Defaults to one scrollport height (100dvh). */
    readonly scrollToTopOffset = input<number | null>(null);

    readonly scrolled = signal(false);

    ngOnInit(): void {
        const element = this.elementRef.nativeElement;

        element.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
        this.destroyRef.onDestroy(() => element.removeEventListener('scroll', this.onScroll, { capture: true }));
    }

    private readonly onScroll = (event: Event): void => {
        const host = this.elementRef.nativeElement;
        const { target } = event;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (target === host) {
            this.scrolled.set(host.scrollTop > 0);
            this.scrollService.activateScroller(host, this.scrollToTopOffset());
        } else if (isPageScroller(target, host)) {
            this.scrollService.activateScroller(target, this.scrollToTopOffset());
        } else {
            this.scrollService.releaseScroller(target);
        }
    };
}

function isPageScroller(element: HTMLElement, host: HTMLElement): boolean {
    return element.clientHeight >= PAGE_SCROLLER_MIN_HEIGHT_RATIO * host.clientHeight && hasScrollableOverflowY(element);
}
